import { useState } from 'react';
import type { CatalogProduct } from '~/components/common/CatalogProductSearch';
import {
  useCatalogLengths,
  type CatalogLengthOption,
} from '~/api/productLengths';
import {
  useGetCannedTallyLengthsForCategory,
  useGetPlatformProductCannedTallies,
} from '~/api/canned-tally';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import type { Quantity } from '~/graphql/graphql';
import {
  NO_CHOICE,
  initialChoiceOf,
  lengthOfChoice,
  pickOf,
  quantityOfChoice,
  type CannedTallyOption,
  type InitialProductLengthPick,
  type LengthLine,
  type ProductLengthChoice,
} from '~/components/common/productLengthChoice';

const NO_PRODUCT = {
  id: '',
  category: null,
  standardUnitsCode: 'PCS',
} as const;

type ProductLengthPickOptions = Readonly<{
  transportVehicleId: string | null;
  cannedTallyOrganizationId?: string | null;
  initialPick?: InitialProductLengthPick;
  onCompleteChange?: (isComplete: boolean) => void;
  pickSeveralLengths?: boolean;
}>;

const NO_LINES: readonly LengthLine[] = [];

function tickedLinesOf(
  choice: ProductLengthChoice,
  onlyLength: CatalogLengthOption | undefined
): readonly LengthLine[] {
  if (choice.kind === 'lengths') return choice.lines;
  if (choice.kind === 'length') {
    const { length, quantity, piecesPerBundle } = choice;
    return [{ length, quantity, piecesPerBundle }];
  }
  if (choice.kind === 'none' && onlyLength)
    return [{ length: onlyLength, quantity: null }];
  return NO_LINES;
}

function choiceOfTickedLines(lines: LengthLine[]): ProductLengthChoice {
  const [firstLine, ...otherLines] = lines;
  if (!firstLine) return NO_CHOICE;
  if (otherLines.length === 0) return { ...firstLine, kind: 'length' };
  return { kind: 'lengths', lines };
}

function toggledLines(
  lines: readonly LengthLine[],
  length: CatalogLengthOption
): LengthLine[] {
  return lines.some((line) => line.length.id === length.id)
    ? lines.filter((line) => line.length.id !== length.id)
    : [...lines, { length, quantity: null }];
}

function activeChoiceOn(
  choice: ProductLengthChoice,
  transportVehicleId: string | null
): ProductLengthChoice {
  return choice.kind === 'canned' &&
    choice.transportVehicleId !== transportVehicleId
    ? NO_CHOICE
    : choice;
}

/**
 * The product, length and quantity (or tally) being picked in one add row, and the
 * pick they make. Render the row with `<ProductLengthQuantityFields key={resetKey}
 * fields={fields} />`. A canned tally is dropped when `transportVehicleId` changes;
 * the canned tallies offered are those of `cannedTallyOrganizationId`, or the
 * platform defaults. `initialPick` sets the first product, length and quantity.
 * `onCompleteChange` hears whether the pick is complete after each buyer change.
 * `pickSeveralLengths` lets the buyer tick lengths: one ticked length is a `length` pick
 * as usual, and two or more give each its own quantity and make a `lengths` pick of the
 * ones with a quantity.
 *
 * @example
 *   const { pick, fields, resetKey, reset } = useProductLengthPick({
 *     transportVehicleId: buyList.transportVehicle.id,
 *   });
 */
export function useProductLengthPick({
  transportVehicleId,
  cannedTallyOrganizationId,
  initialPick,
  onCompleteChange,
  pickSeveralLengths = false,
}: ProductLengthPickOptions) {
  const [product, setProduct] = useState<CatalogProduct | null>(
    initialPick ? initialPick.product : null
  );
  const [chosen, setChosen] = useState<ProductLengthChoice>(() =>
    initialPick ? initialChoiceOf(initialPick) : NO_CHOICE
  );
  const [resetKey, setResetKey] = useState(0);
  const choice = activeChoiceOn(chosen, transportVehicleId);

  const { id: productId, category, standardUnitsCode } = product ?? NO_PRODUCT;
  const hasBundleSize = !!product && product.piecesPerBundle.value > 0;
  const preferredUnit = usePreferredUnit(category, standardUnitsCode);
  const {
    data: firstLengthPage,
    isPlaceholderData,
    error: lengthsError,
  } = useCatalogLengths(0, 1, { platformProductId: productId }, undefined, {
    enabled: !!product,
  });
  const { data: cannedTalliesData } = useGetPlatformProductCannedTallies(
    hasBundleSize ? productId : undefined,
    transportVehicleId ?? undefined,
    cannedTallyOrganizationId
  );
  const { data: candidateLengthsData } = useGetCannedTallyLengthsForCategory(
    hasBundleSize ? productId : undefined
  );

  const cannedTallies =
    (hasBundleSize &&
      transportVehicleId &&
      cannedTalliesData &&
      cannedTalliesData.platformProductCannedTallies) ||
    [];
  const candidateLengthIds = (
    (candidateLengthsData &&
      candidateLengthsData.cannedTallyLengthsForCategory) ??
    []
  ).map((candidate) => candidate.length.id);
  const lengthCount =
    product && firstLengthPage && !isPlaceholderData
      ? firstLengthPage.lengths.paging.totalElements
      : undefined;
  const onlyLength =
    lengthCount === 1 && firstLengthPage
      ? firstLengthPage.lengths.content[0]
      : undefined;
  const context = { onlyLength, candidateLengthIds, preferredUnit };
  const contextBeforeLoading = {
    onlyLength: undefined,
    candidateLengthIds: [],
    preferredUnit,
  };
  const length = lengthOfChoice(choice, onlyLength);
  const productLength = length && length.platformProductLength;
  const tickedLines = tickedLinesOf(choice, onlyLength);
  const severalLines = choice.kind === 'lengths' ? choice.lines : NO_LINES;

  const change = (
    nextProduct: CatalogProduct | null,
    nextChoice: ProductLengthChoice
  ) => {
    setProduct(nextProduct);
    setChosen(nextChoice);
    if (onCompleteChange) {
      const nextContext =
        nextProduct === product ? context : contextBeforeLoading;
      onCompleteChange(pickOf(nextProduct, nextChoice, nextContext).isComplete);
    }
  };

  const reset = () => {
    change(null, NO_CHOICE);
    setResetKey((key) => key + 1);
  };

  const fields = {
    product,
    choice,
    length,
    productLength,
    quantity: quantityOfChoice(choice, !!productLength, preferredUnit),
    hasBundleSize,
    canPickRandomTally: hasBundleSize && !!candidateLengthsData,
    lengthCount,
    onlyLength,
    lengthsError,
    cannedTallies,
    changeProduct: (nextProduct: CatalogProduct | null) =>
      change(nextProduct, NO_CHOICE),
    pickLength: (nextLength: CatalogLengthOption) =>
      change(
        product,
        pickSeveralLengths
          ? choiceOfTickedLines(toggledLines(tickedLines, nextLength))
          : { kind: 'length', length: nextLength, quantity: null }
      ),
    selectedLengthIds: pickSeveralLengths
      ? new Set(tickedLines.map((line) => line.length.id))
      : undefined,
    lengthLines: severalLines.map((line) => ({
      ...line,
      quantity: line.quantity ?? { value: 0, unit: preferredUnit },
    })),
    dropLengths: (platformProductsLengthIds: ReadonlySet<string>) =>
      change(
        product,
        choiceOfTickedLines(
          severalLines.filter(
            (line) =>
              !platformProductsLengthIds.has(
                line.length.platformProductLength.id
              )
          )
        )
      ),
    changeLineQuantity: (lengthId: string, nextQuantity: Quantity) =>
      change(product, {
        kind: 'lengths',
        lines: severalLines.map((line) =>
          line.length.id === lengthId
            ? { ...line, quantity: nextQuantity }
            : line
        ),
      }),
    changeQuantity: (nextQuantity: Quantity) => {
      if (choice.kind === 'length') {
        change(product, { ...choice, quantity: nextQuantity });
      } else if (length) {
        change(product, { kind: 'length', length, quantity: nextQuantity });
      }
    },
    pickCannedTally: (cannedTally: CannedTallyOption, tallyCount: number) =>
      change(product, {
        kind: 'canned',
        cannedTally,
        tallyCount,
        transportVehicleId,
      }),
    changeTallyCount: (tallyCount: number) => {
      if (choice.kind === 'canned') change(product, { ...choice, tallyCount });
    },
    pickRandomTally: (bundleCountsByLengthId: Record<string, string>) =>
      change(product, { kind: 'random', bundleCountsByLengthId }),
    changeBundleCount: (lengthId: string, bundleCount: string) => {
      if (choice.kind !== 'random') return;
      change(product, {
        kind: 'random',
        bundleCountsByLengthId: {
          ...choice.bundleCountsByLengthId,
          [lengthId]: bundleCount,
        },
      });
    },
  };

  return {
    pick: pickOf(product, choice, context),
    fields,
    resetKey,
    reset,
  };
}

export type ProductLengthFieldsState = ReturnType<
  typeof useProductLengthPick
>['fields'];
